import pluralize from 'pluralize';
import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { FiX } from 'react-icons/fi';
import {
  AccommodationRoom,
  AvailableDate,
  BookingFormValues,
  EnumPriceUnit,
  PriceUnit,
  Option,
  Price,
  TMoney,
  EnumOptionAccommodationSaleType,
  EnumAccommodationRoomType,
  EnumAccommodationRoomOccupancyType,
  Translation,
  SelectedAccommodationRoom,
  RecommendedAccommodationRoom,
  OrderItemAccommodationRoom,
} from 'src/types';
import ImageWithFallback from '../ImageWithFallback';
import Stepper from 'src/components/Stepper';
import Money from 'src/components/Money';
import { useFormikContext } from 'formik';
import { getTotalQty } from 'src/helpers';
import { resolvePrice } from 'src/lib/price_resolution';
import { sortBy, capitalize, range, min, max, flatten } from 'lodash-es';
import classNames from 'classnames';
import useModalAccommodationRoomContext from 'src/hooks/useModalAccommodationRoomContext';
import useCurrentCurrency from 'src/hooks/useCurrentCurrency';
import { ZERO_AMOUNT } from 'src/lib/constants';
import useCart from 'src/hooks/useCart';
import { separateOrderItemAccommodationRooms, getAccommodationRoomPriceByDate, getAccommodationRoomPriceMultiplier } from '../OptionDatepicker';
import { useTranslations } from 'next-intl';
import DisplayPriceUnit from 'src/components/DisplayPriceUnit';

const getNumMulti = (priceUnit: PriceUnit, qty: number, numOfPerson: number, occupancyType?: 'private' | 'shared') => {
  return priceUnit === EnumPriceUnit.perRoom
    ? !occupancyType || occupancyType === 'private' ? qty : numOfPerson
    : priceUnit === EnumPriceUnit.perPerson
    ? numOfPerson
    : 1;
};

type Props = {
  isGiftExperience?: boolean;
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  accommodationRooms: AccommodationRoom[];
  availableDates: AvailableDate[];
  startDate: string;
  prices: Price[];
  option: Option;
};

type SelectedAccommodationRoomCardProps = {
  isGiftExperience?: boolean;
  accommodationRoom: AccommodationRoom;
  accommodationRoomCap: { [key: number]: number | undefined };
  currentQty: number | number[];
  setSelectedAccommodationRooms: React.Dispatch<React.SetStateAction<SelectedAccommodationRoom[]>>;
  numOfPerson: number | number[];
  totalNumPersons: number;
  isRecommended?: boolean;
  recommendedAmount?: TMoney;
  option: Option;
  prices: Price[];
  startDate: string;
};

const SelectAccommodationRoomCard: React.FC<SelectedAccommodationRoomCardProps> = ({
  isGiftExperience,
  accommodationRoom,
  accommodationRoomCap,
  currentQty: currentQties,
  setSelectedAccommodationRooms,
  numOfPerson: numOfPersons,
  totalNumPersons,
  isRecommended,
  recommendedAmount,
  prices,
  option,
  startDate,
}) => {
  const currentQty = Array.isArray(currentQties) ? currentQties.reduce((memo, v) => memo + v, 0) : currentQties
  const numOfPerson = Array.isArray(numOfPersons) ? max(numOfPersons) as number : numOfPersons
  const translation = useTranslations('components.option.ModalSelectAccommodation')
  const currentCurrency = useCurrentCurrency();
  const { setShow, setViewAccommodationRoomId } = useModalAccommodationRoomContext();
  const [showInput, setShowInput] = useState(false);
  const resolvedAmount =
    currentQty > 0 && numOfPerson > 0
      ? resolvePrice(
          prices,
          option,
          accommodationRoom.id,
          startDate,
          'foreigner',
          'adult',
          numOfPerson
        ).price
      : resolvePrice(
          prices,
          option,
          accommodationRoom.id,
          startDate,
          'foreigner',
          'adult',
          numOfPerson > 0 ? min([totalNumPersons, numOfPerson]) as number : totalNumPersons
        ).price
  const totalQty = getNumMulti(option.priceUnit, currentQty, numOfPerson, accommodationRoom.occupancyType);
  const roomUnit = accommodationRoom.occupancyType === EnumAccommodationRoomOccupancyType.private ? translation('room') : translation('spot');
  const totalAmount =
    isRecommended && recommendedAmount
      ? recommendedAmount.amount
      : Array.isArray(numOfPersons) && Array.isArray(currentQties)
      ? numOfPersons.reduce((memo, num, index) => {
          return memo + Number(resolvePrice(
            prices,
            option,
            accommodationRoom.id,
            startDate,
            'foreigner',
            'adult',
            num
          ).price.amount) * currentQties[index]
        }, 0)
      : Number(resolvedAmount.amount) * totalQty;

  useEffect(() => {
    if (currentQty > 0) {
      setShowInput(true);
    }
  }, [currentQty]);
  return (
    <div key={accommodationRoom.id}>
      <div className="tw-flex tw-w-full">
        <ImageWithFallback
          record={accommodationRoom.images[0]}
          imgField="webpSmUrl"
          fill
          alt={accommodationRoom.images[0]?.caption || accommodationRoom.name}
          className="tw-static tw-block tw-h-20 tw-w-20 tw-mr-3 tw-rounded-lg tw-cursor-pointer tw-object-cover tw-flex-shrink-0 tw-border-2 tw-border-brand-green tw-border-solid"
          onClick={() => {
            setShow(true);
            setViewAccommodationRoomId(accommodationRoom.id);
          }}
        />
        <div className="tw-flex tw-flex-col tw-justify-between tw-w-full">
          <div
            onClick={() => {
              setShow(true);
              setViewAccommodationRoomId(accommodationRoom.id);
            }}
            className="tw-cursor-pointer tw-flex tw-justify-between"
          >
            <div>
              <div className="tw-font-semibold tw-font-styleSans tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none">
                {isRecommended ? accommodationRoom.occupancyType === EnumAccommodationRoomOccupancyType.shared ? `${numOfPerson} x ${translation('bedIn')} ` : `${currentQty} x ` : ''}
                {accommodationRoom.name}
              </div>
              <div className="tw-text-sm">
                {translation('sleeps')} {accommodationRoom.maxNumOfPerson} {accommodationRoom.occupancyType === EnumAccommodationRoomOccupancyType.shared ? translation('perRoom') : ''}
              </div>
            </div>
            {typeof accommodationRoomCap[accommodationRoom.id] === 'number' ? (
              <div className="tw-text-red tw-text-xs tw-ml-3 tw-flex-shrink-0">
                {pluralize(roomUnit, accommodationRoomCap[accommodationRoom.id], true)} {translation('left')}
              </div>
            ) : null}
          </div>
          <div className="tw-flex tw-w-full tw-items-center tw-justify-between">
            <div>
              {currentQty > 0 && numOfPerson > 0 ? (
                <span className="tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none tw-text-lg md:tw-text-xl tw-mr-2">{translation('total')}:</span>
              ) : null}
              <Money
                amount={
                  currentQty > 0 && numOfPerson > 0
                    ? {
                      amount: totalAmount,
                      currency: option.baseCurrency,
                    }
                    : resolvedAmount
                }
                className="tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none tw-text-lg md:tw-text-xl"
                toCurrency={currentCurrency}
                hasConversionFee={!isGiftExperience && option.baseCurrency !== currentCurrency}
              />
              {currentQty > 0 && numOfPerson > 0 ? null : (
                <span className="tw-text-sm">
                  <DisplayPriceUnit
                    option={option}
                    priceUnit={
                      accommodationRoom.occupancyType === EnumAccommodationRoomOccupancyType.private
                        ? `/${translation('room')}`
                        : `/${translation('person')}`
                    }
                  />
                </span>
              )}
            </div>
            {!isRecommended ? (
              <>
                {!showInput ? (
                  <div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setShowInput(true);
                        setSelectedAccommodationRooms((prev) => {
                          return prev.map((o) => {
                            return o.accommodationRoomId === accommodationRoom.id
                              ? {
                                  ...o,
                                  qty: 1,
                                  numOfPerson: min([totalNumPersons, o.maxNumOfPerson]) as number,
                                }
                              : o;
                          });
                        });
                      }}
                    >
                      {translation('select')}
                    </button>
                  </div>
                ) : currentQty > 0 ? (
                  <div
                    className="tw-text-xs tw-text-grey tw-lowercase tw-underline tw-cursor-pointer"
                    onClick={() => {
                      setSelectedAccommodationRooms((prev) => {
                        return prev.map((o) => {
                          return o.accommodationRoomId === accommodationRoom.id
                            ? {
                                ...o,
                                qty: 0,
                                numOfPerson: 0,
                              }
                            : o;
                        });
                      });
                      setShowInput(false);
                    }}
                  >
                    {translation('remove')}
                  </div>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>

      {!isRecommended ? (
        <div className={classNames('tw-mt-4', { 'tw-hidden': !showInput })}>
          {accommodationRoom.occupancyType === EnumAccommodationRoomOccupancyType.shared ? (
            <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-mb-4">
              <div>{translation('numberOfPersons')}</div>
              <Stepper
                inputDisabled={true}
                value={numOfPerson}
                minusClass={
                  numOfPerson <= 1
                    ? 'tw-cursor-not-allowed tw-text-grey-lighter'
                    : 'tw-text-brand-navy tw-cursor-pointer'
                }
                plusClass={
                  numOfPerson >= (accommodationRoomCap[accommodationRoom.id] || 0) || numOfPerson >= totalNumPersons
                    ? 'tw-cursor-not-allowed tw-text-grey-lighter'
                    : 'tw-text-brand-navy tw-cursor-pointer'
                }
                onIncrease={() => {
                  if (numOfPerson >= (accommodationRoomCap[accommodationRoom.id] || 0)) return;
                  if (numOfPerson >= totalNumPersons) return;
                  setSelectedAccommodationRooms((prev) => {
                    return prev.map((o) => {
                      if (o.accommodationRoomId === accommodationRoom.id) {
                        const numOfPerson = min([o.numOfPerson + 1, accommodationRoom.maxNumOfPerson * accommodationRoom.qty, totalNumPersons]) as number
                        const qty = numOfPerson > accommodationRoom.maxNumOfPerson * o.qty ? o.qty + 1 : o.qty
                        return {
                          ...o,
                          qty: qty,
                          numOfPerson: numOfPerson
                        }
                      } else {
                        return o
                      }
                    });
                  });
                }}
                onDecrease={() => {
                  if (numOfPerson <= 1) return;
                  setSelectedAccommodationRooms((prev) => {
                    return prev.map((o) => {
                      if (o.accommodationRoomId === accommodationRoom.id) {
                        const numOfPerson = max([o.numOfPerson - 1, 0]) as number
                        const qty = (o.qty > 1 && numOfPerson <= (accommodationRoom.maxNumOfPerson * (o.qty - 1)) ? o.qty - 1 : o.qty)

                        return {
                          ...o,
                          qty: qty,
                          numOfPerson: numOfPerson
                        }
                      } else {
                        return o
                      }
                    });
                  });
                }}
              />
            </div>
          ) : (
            <>
              <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-mb-4">
                <div>{translation('numberOfRooms')}</div>
                <Stepper
                  inputDisabled={true}
                  value={currentQty}
                  minusClass={
                    currentQty <= 1
                      ? 'tw-cursor-not-allowed tw-text-grey-lighter'
                      : 'tw-text-brand-navy tw-cursor-pointer'
                  }
                  plusClass={
                    currentQty >= (accommodationRoomCap[accommodationRoom.id] || 0)
                      ? 'tw-cursor-not-allowed tw-text-grey-lighter'
                      : 'tw-text-brand-navy tw-cursor-pointer'
                  }
                  onIncrease={() => {
                    if (currentQty >= (accommodationRoomCap[accommodationRoom.id] || 0)) return;
                    setSelectedAccommodationRooms((prev) => {
                      return prev.map((o) => {
                        return o.accommodationRoomId === accommodationRoom.id
                          ? {
                            ...o,
                            qty: o.qty + 1,
                            numOfPerson:
                              o.qty === 0 && o.numOfPerson === 0 ? o.maxNumOfPerson : o.numOfPerson,
                          } : o
                      });
                    });
                  }}
                  onDecrease={() => {
                    if (currentQty <= 1) return;
                    setSelectedAccommodationRooms((prev) => {
                      return prev.map((o) => {
                        return o.accommodationRoomId === accommodationRoom.id ? {
                          ...o,
                          qty: o.qty - 1,
                          numOfPerson: o.qty - 1 === 0 ? 0 : o.numOfPerson,
                        } : o
                      });
                    });
                  }}
                />
              </div>
              <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                <div>{translation('guestsPerRoom')}</div>
                <Stepper
                inputDisabled={true}
                value={numOfPerson}
                minusClass={
                  numOfPerson <= 1
                  ? 'tw-cursor-not-allowed tw-text-grey-lighter'
                  : 'tw-text-brand-navy tw-cursor-pointer'
                }
                plusClass={
                  currentQty <= 0 || numOfPerson >= accommodationRoom.maxNumOfPerson
                  ? 'tw-cursor-not-allowed tw-text-grey-lighter'
                  : 'tw-text-brand-navy tw-cursor-pointer'
                }
                onIncrease={() => {
                  if (currentQty <= 0 || numOfPerson >= accommodationRoom.maxNumOfPerson) return;
                  setSelectedAccommodationRooms((prev) => {
                    return prev.map((o) => {
                      return o.accommodationRoomId === accommodationRoom.id
                      ? {
                        ...o,
                        numOfPerson: o.numOfPerson + 1,
                      }
                      : o;
                    });
                  });
                }}
                onDecrease={() => {
                  if (numOfPerson <= 1) return;
                  setSelectedAccommodationRooms((prev) => {
                    return prev.map((o) => {
                      return o.accommodationRoomId === accommodationRoom.id
                      ? {
                        ...o,
                        numOfPerson: o.numOfPerson - 1,
                      }
                      : o;
                    });
                  });
                }}
                />
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

const validateRoomSelection: (
  selectedAccommodationRooms: SelectedAccommodationRoom[],
  totalNumPersons: number
) => string | undefined = (selectedAccommodationRooms, totalNumPersons) => {
  const numPersonWithAccommodation = selectedAccommodationRooms.reduce((memo, obj) => {
    return memo + (obj.occupancyType === EnumAccommodationRoomOccupancyType.shared ? obj.numOfPerson : obj.numOfPerson * obj.qty);
  }, 0);
  const numPersonWithoutAccommodation = totalNumPersons - numPersonWithAccommodation;
  if (numPersonWithoutAccommodation > 0) {
    return String(numPersonWithoutAccommodation);
  }
};

export const mergeRecommendedAccommodationRooms = (
  recommendedAccommodationRooms: RecommendedAccommodationRoom[],
  option: Option
) => {
  return option.priceUnit === EnumPriceUnit.perRoom
    ? recommendedAccommodationRooms.reduce((memo, obj) => {
        let currentObj = memo.find((o) => o.accommodationRoomId === obj.accommodationRoomId);
        let merged = false;

        if (currentObj) {
          merged = true;
          currentObj = {
            ...currentObj,
            qty: currentObj.qty + obj.qty,
            amount: {
              amount: Number(currentObj.amount.amount) + Number(obj.amount.amount),
              currency: currentObj.amount.currency,
            },
          };
        } else {
          currentObj = obj;
        }

        return merged
          ? memo.map((o) =>
              o.accommodationRoomId === obj.accommodationRoomId
                ? (currentObj as RecommendedAccommodationRoom)
                : o
            )
          : memo.concat([currentObj]);
      }, [] as RecommendedAccommodationRoom[])
    : recommendedAccommodationRooms;
};

const ModalSelectAccommodation: React.FC<Props> = ({
  isGiftExperience,
  show,
  setShow,
  accommodationRooms,
  availableDates,
  startDate,
  prices,
  option,
}) => {
  const [isAvailableDatesFullyLoaded, setIsAvailableDatesFullyLoaded] = useState(false)
  const translation = useTranslations('components.option.ModalSelectAccommodation')
  const currentCurrency = useCurrentCurrency();
  const [error, setError] = useState<string>();
  const [currentCart] = useCart();
  const orderItem = currentCart.orderItem;
  const [orderItemLoaded, setOrderItemLoaded] = useState(false);
  const { values, setFieldValue } = useFormikContext<BookingFormValues>();
  const validOrderItemAccommodationRooms = accommodationRooms.map((accommodationRoom) => {
    const orderItemAccommodationRoom = mergeRecommendedAccommodationRooms(values.orderItemAccommodationRooms, option).find(
      (o) => o.accommodationRoomId === accommodationRoom.id
    );
    return orderItemAccommodationRoom
      ? orderItemAccommodationRoom
      : {
          accommodationRoomName: accommodationRoom.name,
          accommodationRoomId: accommodationRoom.id,
          qty: option.accommodationSaleType === EnumOptionAccommodationSaleType.pickRoom ? 0 : 1,
          roomQty: accommodationRoom.qty,
          numOfPerson: 0,
          maxNumOfPerson: accommodationRoom.maxNumOfPerson,
          occupancyType: accommodationRoom.occupancyType,
          masterAmount: ZERO_AMOUNT,
          amount: ZERO_AMOUNT,
        } as OrderItemAccommodationRoom;
  });
  const [selectedAccommodationRooms, setSelectedAccommodationRooms] = useState<
    SelectedAccommodationRoom[]
  >(
    accommodationRooms.map((accommodationRoom) => {
      const orderItemAccommodationRoom = validOrderItemAccommodationRooms.find(
        (o) => accommodationRoom.id === o.accommodationRoomId
      );
      return {
        accommodationRoomId: accommodationRoom.id,
        qty: orderItemAccommodationRoom?.qty || 0,
        occupancyType: accommodationRoom.occupancyType,
        maxNumOfPerson: accommodationRoom.maxNumOfPerson,
        numOfPerson: orderItemAccommodationRoom?.numOfPerson || 0,
        amount: ((Number(orderItemAccommodationRoom?.amount?.amount) || 0) > 0
          ? orderItemAccommodationRoom?.amount
          : accommodationRoom.minAmount) as TMoney,
      };
    })
  );
  const numSelectedRooms = selectedAccommodationRooms.reduce((memo, o) => memo + o.qty, 0);
  const totalNumPersons = getTotalQty(values) || 2;
  const accommodationRoomCap = accommodationRooms.reduce((memo, accommodationRoom) => {
    const capacity =
      availableDates.find(
        (o) => o.accommodationRoomId === accommodationRoom.id && o.date === startDate
      )?.capacity || 0;
    memo[accommodationRoom.id] = capacity;
    return memo;
  }, {} as { [key: number]: number | undefined });

  const sortedAccommodationRooms = sortBy(accommodationRooms, (accommodationRoom) => {
    return Number(accommodationRoom.minAmount.amount);
  });

  const sortedRecommendedAccommodationRooms = sortBy(accommodationRooms, (accommodationRoom) => {
    const totalQty = getTotalQty(values)
    return totalQty > 0 ? Number(
      resolvePrice(
        prices,
        option,
        accommodationRoom.id,
        startDate,
        'foreigner',
        'adult',
        totalQty
      ).price.amount
    ) : Number(accommodationRoom.minAmount.amount);
  });

  const recommendedAccommodationRooms =
    option.priceUnit === EnumPriceUnit.perRoom
      ? getAccommodationRoomPriceByDate(
          prices,
          option,
          startDate,
          {
            quantity: totalNumPersons,
            localQuantity: 0,
            childQuantity: 0,
            childLocalQuantity: 0,
            orderItemAccommodationRooms: validOrderItemAccommodationRooms,
          },
          availableDates
        ).map((obj) => {
          const multiplier = getAccommodationRoomPriceMultiplier(obj)
          return {
            accommodationRoomId: obj.price.accommodationRoomId as number,
            qty: obj.qty,
            maxNumOfPerson: obj.maxNumOfPerson,
            occupancyType: obj.occupancyType,
            numOfPerson: obj.numOfPerson,
            amount: {
              amount: Number(obj.price.price.amount) * multiplier,
              currency: obj.price.price.currency
            },
          };
        })
      : sortedRecommendedAccommodationRooms.reduce((memo, accommodationRoom) => {
          const numOfPersonWithAccommodation = memo.reduce(
            (total, o) => total + o.qty * o.maxNumOfPerson,
            0
          );
          if (numOfPersonWithAccommodation >= totalNumPersons) {
            return memo;
          }

          const numOfPersonWithoutAccommodation = totalNumPersons - numOfPersonWithAccommodation;
          const capacity = accommodationRoomCap[accommodationRoom.id];
          if (!capacity) return memo;

          let tmpCapacity = capacity;
          while (true) {
            if (tmpCapacity * accommodationRoom.maxNumOfPerson >= numOfPersonWithoutAccommodation) {
              tmpCapacity -= 1;
            } else {
              break;
            }
          }
          tmpCapacity = tmpCapacity < capacity ? tmpCapacity + 1 : tmpCapacity;
          const isFirstItem = memo.length === 0
          const numOfPerson = isFirstItem && totalNumPersons < accommodationRoom.maxNumOfPerson ? totalNumPersons : accommodationRoom.maxNumOfPerson
          const resolvedAmount = resolvePrice(
            prices,
            option,
            accommodationRoom.id,
            startDate,
            'foreigner',
            'adult',
            numOfPerson
          ).price;
          const numMulti = getNumMulti(
            option.priceUnit,
            tmpCapacity,
            accommodationRoom.maxNumOfPerson,
            accommodationRoom.occupancyType
          );

          return memo.concat([
            {
              accommodationRoomId: accommodationRoom.id,
              qty: tmpCapacity,
              maxNumOfPerson: accommodationRoom.maxNumOfPerson,
              occupancyType: accommodationRoom.occupancyType,
              numOfPerson: numOfPerson,
              amount: {
                amount: Number(resolvedAmount.amount) * numMulti,
                currency: resolvedAmount.currency,
              },
            },
          ]);
        }, [] as SelectedAccommodationRoom[]);

  const numPersonWithAccommodation = selectedAccommodationRooms.reduce((memo, obj) => {
    return memo + obj.numOfPerson * obj.qty;
  }, 0);

  useEffect(() => {
    const latestSelectedAccommodationRooms = separateOrderItemAccommodationRooms(selectedAccommodationRooms, getTotalQty(values))
    setSelectedAccommodationRooms((prev) => {
      return prev.map((o) => {
        const currentSelectedAccommodationRooms = latestSelectedAccommodationRooms.filter(v => v.accommodationRoomId === o.accommodationRoomId)
        return {
          ...o,
          qty: currentSelectedAccommodationRooms.reduce((memo, o) => memo + o.qty, 0),
          numOfPerson: max(currentSelectedAccommodationRooms.map(o => o.numOfPerson)) as number
        }
      })
    })
  }, [JSON.stringify(selectedAccommodationRooms)])

  useEffect(() => {
    const update = () => {
      setIsAvailableDatesFullyLoaded(true)
    }
    window.document.addEventListener('ss:trip_available_dates_fully_loaded', update, { once: true })

    return () => {
      window.document.removeEventListener('ss:trip_available_dates_fully_loaded', update)
    }
  }, [])

  useEffect(() => {
    setSelectedAccommodationRooms(
      accommodationRooms.map((accommodationRoom) => {
        const orderItemAccommodationRoom = validOrderItemAccommodationRooms.find(
          (o) => accommodationRoom.id === o.accommodationRoomId
        );
        return {
          accommodationRoomId: accommodationRoom.id,
          qty: orderItemAccommodationRoom?.qty || 0,
          maxNumOfPerson: accommodationRoom.maxNumOfPerson,
          occupancyType: accommodationRoom.occupancyType,
          numOfPerson: orderItemAccommodationRoom?.numOfPerson || 0,
          amount: ((Number(orderItemAccommodationRoom?.amount?.amount) || 0) > 0
            ? orderItemAccommodationRoom?.amount
            : accommodationRoom.minAmount) as TMoney,
        };
      })
    );
  }, [JSON.stringify(validOrderItemAccommodationRooms)]);

  useEffect(() => {
    const maxCap = availableDates
      .filter((o) => o.date === values.startDate)
      .reduce((total, o) => total + (o.accommodationRoomOccupancyType === EnumAccommodationRoomOccupancyType.shared ? o.capacity : o.capacity * o.maxNumOfPerson), 0);
    if (isAvailableDatesFullyLoaded && getTotalQty(values) > maxCap) {
      setFieldValue('startDate', null);
    }
    if (orderItem?.id && getTotalQty(values)) {
      setOrderItemLoaded(true);
    }

    const isChanged =
      orderItemLoaded &&
      (getTotalQty(orderItem || values) !== getTotalQty(values) ||
        orderItem?.startDate !== values.startDate);

    if (!orderItem?.id || isChanged) {
      setFieldValue(
        'orderItemAccommodationRooms',
        validOrderItemAccommodationRooms.map((o) => ({
          ...o,
          qty: 0,
          numOfPerson: 0,
        }))
      );
    }
  }, [
    isAvailableDatesFullyLoaded,
    getTotalQty(values),
    values.startDate,
    availableDates.length,
    orderItem?.id,
    orderItem?.startDate,
  ]);

  return (
    <Modal
      show={show}
      className="fade booking-form__modal"
      tabIndex={-1}
      dialogClassName="modal-full-width--sp md:tw-max-w-2xl modal-dialog-scrollable"
    >
      <Modal.Header>
        <div className="tw-flex tw-justify-between tw-items-center tw-w-full">
          <div className="modal-title tw-font-semibold">{translation('chooseAccommodation')}</div>
          <FiX size={24} className="tw-cursor-pointer" onClick={() => setShow(false)} />
        </div>
      </Modal.Header>
      <Modal.Body>
        <div>
          <div className="tw-font-semibold tw-font-styleSans tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none tw-mb-4">
            {translation('lowestPriceFor')} {translation('pluralizePerson', {count: totalNumPersons})}
          </div>
          <div className="tw-border tw-border-grey-lighter tw-rounded-lg tw-p-4 md:tw-p-6 tw-bg-white">
            {mergeRecommendedAccommodationRooms(recommendedAccommodationRooms, option).map(
              (recommendedAccommodationRoom, index) => {
                const accommodationRoom = accommodationRooms.find(
                  (o) => o.id === recommendedAccommodationRoom.accommodationRoomId
                )
                return accommodationRoom ? (
                  <div key={accommodationRoom.id} className={classNames({ 'tw-mt-4': index > 0 })}>
                    <SelectAccommodationRoomCard
                      key={accommodationRoom.id}
                      totalNumPersons={totalNumPersons}
                      isGiftExperience={isGiftExperience}
                      startDate={startDate}
                      prices={prices}
                      option={option}
                      accommodationRoom={accommodationRoom}
                      currentQty={recommendedAccommodationRoom.qty}
                      setSelectedAccommodationRooms={setSelectedAccommodationRooms}
                      accommodationRoomCap={accommodationRoomCap}
                      numOfPerson={recommendedAccommodationRoom.numOfPerson}
                      isRecommended={true}
                      recommendedAmount={recommendedAccommodationRoom.amount}
                    />
                  </div>
                ) : null;
              }
            )}
            <div className="tw-my-5 tw-border-t tw-border-grey-lighter"></div>
            <div className="tw-flex tw-justify-between">
              <div>
                <div className="tw-font-semibold tw-font-styleSans tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none">
                  {translation('total')}: <Money
                    amount={{
                      amount: recommendedAccommodationRooms.reduce((total, o) => {
                        return total + Number(o.amount?.amount || 0);
                      }, 0),
                      currency: option.baseCurrency,
                    }}
                    toCurrency={currentCurrency}
                    hasConversionFee={!isGiftExperience && option.baseCurrency !== currentCurrency}
                  />
                </div>
                <div className="tw-text-sm">
                  {translation('for')} {translation('pluralizePerson', {count: totalNumPersons})}
                </div>
              </div>
              <div>
                <button
                  onClick={() => {
                    const tmp = recommendedAccommodationRooms
                      .map((recommendedAccommodationRoom) => {
                        const orderItemAccommodationRoom = validOrderItemAccommodationRooms.find(
                          (obj) =>
                            obj.accommodationRoomId ===
                            recommendedAccommodationRoom.accommodationRoomId
                        );
                        return orderItemAccommodationRoom
                          ? {
                              ...orderItemAccommodationRoom,
                              qty: recommendedAccommodationRoom.qty,
                              numOfPerson: recommendedAccommodationRoom.numOfPerson,
                              amount: recommendedAccommodationRoom.amount,
                            }
                          : undefined;
                      })
                      .filter((v) => v)
                      .concat(
                        validOrderItemAccommodationRooms.filter((o) => {
                          const ids = recommendedAccommodationRooms.map(
                            (v) => v.accommodationRoomId
                          );
                          return ids.includes(o.accommodationRoomId) ? undefined : o;
                        })
                      )
                      .filter((v) => v) as OrderItemAccommodationRoom[];
                    setFieldValue('orderItemAccommodationRooms', tmp);
                    setShow(false);
                  }}
                  type="button"
                  className="btn btn-primary"
                >
                  {translation('select')}
                </button>
              </div>
            </div>
          </div>

          <div className="tw-flex tw-items-center tw-my-5">
            <div className="tw-border-t tw-w-full tw-border-grey-lighter tw-border-solid"></div>
            <div className="tw-px-3 tw-font-semibold tw-text-lg">{translation('or')}</div>
            <div className="tw-border-t tw-w-full tw-border-grey-lighter tw-border-solid"></div>
          </div>

          <div className="tw-font-semibold tw-font-styleSans tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none tw-mb-4">
            {translation('pickYourFavoriteRooms')}
          </div>
          {sortedAccommodationRooms.map((accommodationRoom, index) => {
            const currentSelectedAccommodationRooms = separateOrderItemAccommodationRooms(selectedAccommodationRooms, getTotalQty(values)).filter(
              (o) => o.accommodationRoomId === accommodationRoom.id
            ) as SelectedAccommodationRoom[];
            return accommodationRoomCap[accommodationRoom.id] ? (
              <div
                key={accommodationRoom.id}
                className={classNames('tw-border tw-border-grey-lighter tw-rounded-lg tw-bg-white tw-p-4 md:tw-p-6', {
                  'tw-mt-4': index > 0,
                })}
              >
                <SelectAccommodationRoomCard
                  key={accommodationRoom.id}
                  startDate={startDate}
                  prices={prices}
                  option={option}
                  accommodationRoom={accommodationRoom}
                  currentQty={currentSelectedAccommodationRooms.length > 1 ? currentSelectedAccommodationRooms.map(o => o.qty) : currentSelectedAccommodationRooms[0].qty}
                  totalNumPersons={totalNumPersons}
                  setSelectedAccommodationRooms={setSelectedAccommodationRooms}
                  accommodationRoomCap={accommodationRoomCap}
                  numOfPerson={currentSelectedAccommodationRooms.length > 1 ? currentSelectedAccommodationRooms.map(o => o.numOfPerson) : currentSelectedAccommodationRooms[0].numOfPerson}
                />
              </div>
            ) : null;
          })}
          <div className="tw-pb-4"></div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="tw-flex tw-items-start tw-justify-between tw-w-full">
          <div>
            {validateRoomSelection(selectedAccommodationRooms, totalNumPersons) && error ? (
              <div className="tw-text-red tw-text-sm tw-mb-1">{error}</div>
            ) : null}
            {numSelectedRooms > 0 && numPersonWithAccommodation > 0 ? (
              <>
                <div className="tw-font-semibold tw-font-styleSans tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none">
                  {translation('total')}: <Money
                    amount={{
                      amount: separateOrderItemAccommodationRooms(selectedAccommodationRooms, getTotalQty(values)).reduce((total, o) => {
                        const accommodationRoom = accommodationRooms.find(v => v.id === o.accommodationRoomId)
                        const numMulti = getNumMulti(option.priceUnit, o.qty, o.numOfPerson, accommodationRoom?.occupancyType);
                        const resolvedAmount =
                          o.qty > 0 && o.numOfPerson > 0
                            ? resolvePrice(
                              prices,
                              option,
                              o.accommodationRoomId,
                              values.startDate,
                              'foreigner',
                              'adult',
                              o.numOfPerson
                            ).price
                            : ZERO_AMOUNT;
                        const amount = Number(resolvedAmount.amount) * numMulti;
                        return total + amount;
                      }, 0),
                      currency: option.baseCurrency,
                    }}
                    toCurrency={currentCurrency}
                    hasConversionFee={!isGiftExperience && option.baseCurrency !== currentCurrency}
                  />
                </div>
                {selectedAccommodationRooms
                  .filter((o) => o.qty > 0)
                  .map((o, index) => {
                    const accommodationRoom = accommodationRooms.find(
                      (v) => v.id === o.accommodationRoomId
                    ) as AccommodationRoom;
                    return (
                      <div key={o.accommodationRoomId}>
                        <span className="tw-text-sm">
                          {o.occupancyType === EnumAccommodationRoomOccupancyType.shared
                            ? `${o.numOfPerson} x ${translation('bedIn')} ${accommodationRoom.name}`
                            : `${o.qty} x ${accommodationRoom.name}`}
                        </span>
                        <span
                          className="tw-ml-4 tw-text-grey tw-lowercase tw-underline tw-text-xs"
                          onClick={() => {
                            setSelectedAccommodationRooms((prev) => {
                              return prev.map((v) => {
                                return v.accommodationRoomId === accommodationRoom.id
                                  ? {
                                      ...v,
                                      qty: 0,
                                      numOfPerson: 0,
                                    }
                                  : v;
                              });
                            });
                          }}
                        >
                          {translation('remove')}
                        </span>
                      </div>
                    );
                  })}
              </>
            ) : null}
          </div>
          <button
            onClick={() => {
              const fitPersons = validateRoomSelection(selectedAccommodationRooms, totalNumPersons);
              if (fitPersons) {
                setError(translation('youStillNeedToFit', {count: fitPersons}));
                return;
              }
              const value = flatten(validOrderItemAccommodationRooms.map((o) => {
                const filteredAccommodationRooms = separateOrderItemAccommodationRooms(selectedAccommodationRooms, getTotalQty(values)).filter(
                  (obj) => obj.accommodationRoomId === o.accommodationRoomId && obj.qty > 0
                );
                return filteredAccommodationRooms.map((selectedAccommodationRoom) => {
                  const accommodationRoom = accommodationRooms.find(v => v.id === selectedAccommodationRoom?.accommodationRoomId)
                  const qty = selectedAccommodationRoom?.qty || o.qty;
                  const numOfPerson = selectedAccommodationRoom?.numOfPerson || o.numOfPerson;
                  const amount = resolvePrice(
                    prices,
                    option,
                    o.accommodationRoomId,
                    values.startDate,
                    'foreigner',
                    'adult',
                    numOfPerson
                  ).price;
                  const numMulti = getNumMulti(option.priceUnit, qty, numOfPerson, accommodationRoom?.occupancyType);
                  return selectedAccommodationRoom
                    ? {
                        ...o,
                        qty: selectedAccommodationRoom.qty,
                        numOfPerson: selectedAccommodationRoom.numOfPerson,
                        amount: {
                          amount: Number(amount.amount) * numMulti,
                          currency: amount.currency,
                        },
                      }
                    : {
                        ...o,
                        qty: 0,
                        numOfPerson: 0,
                        amount: ZERO_AMOUNT,
                      };
                })
              }))

              setFieldValue('orderItemAccommodationRooms', value);
              setShow(false);
            }}
            type="button"
            className="btn btn-primary tw-rounded-lg"
          >
            {translation('select')}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalSelectAccommodation;
